// ── BCHNostr — Verify your creator channel (one-time) ─────────────
//
// A creator proves they control a YouTube channel so their posted videos
// earn a "Verified creator" badge. Proof = a token, unique to their npub,
// pasted into the description of ONE of their own videos. We read it back
// through the CORS proxy and, on success, publish a signed per-channel
// verification event. This is opt-in and done once per channel — it never
// touches the video posting flow.

import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, BadgeCheck, Copy, Check, Loader2, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrPublish } from '../hooks/useNostrPublish';
import {
  verificationToken, checkVideoDescriptionToken, buildCreatorVerifyEvent,
  verifyPlatformForProvider, verifyPlatformLabel, displayChannel,
} from '../lib/creatorVerify';
import { resolveVideoUrl } from '../lib/videoProviders';

export default function VerifyChannelModal({
  onClose, onVerified, initialVideoUrl = '',
}: {
  onClose: () => void;
  onVerified?: (channel: string) => void;
  /**
   * Pre-fill the "paste your video link" field — used by the post-publish
   * nudge, where the creator just added a video and can drop the token into
   * that same video's description.
   */
  initialVideoUrl?: string;
}) {
  const { user, setShowLoginDialog } = useAppContext();
  const { publish } = useNostrPublish();

  const [videoUrl, setVideoUrl] = useState(initialVideoUrl);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [verifiedChannel, setVerifiedChannel] = useState('');

  const token = user ? verificationToken(user.pubkey) : '';

  // Detect which platform the pasted URL belongs to, so the copy and the
  // "check" both speak about the right service. '' until a verifiable URL
  // is entered.
  const detected = useMemo(() => {
    const resolved = resolveVideoUrl(videoUrl.trim());
    const platform = resolved ? verifyPlatformForProvider(resolved.provider) : null;
    return platform ? verifyPlatformLabel(platform) : '';
  }, [videoUrl]);

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the token and copy it manually.');
    }
  };

  const handleVerify = async () => {
    if (!user) { setShowLoginDialog(true); return; }
    if (!videoUrl.trim()) { setError('Paste the link to the video you added the token to.'); return; }

    setChecking(true);
    setError('');
    try {
      const result = await checkVideoDescriptionToken(videoUrl, user.pubkey);
      if (!result.ok) { setError(result.reason); return; }

      const { kind, tags, content } = buildCreatorVerifyEvent(result.platform, result.channel);
      await publish({ kind, content, tags, created_at: Math.floor(Date.now() / 1000) });
      setVerifiedChannel(result.channel);
      onVerified?.(result.channel);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed. Try again.');
    } finally {
      setChecking(false);
    }
  };

  // ── Logged out ──
  if (!user) {
    return createPortal(
      <Backdrop onClose={onClose}>
        <Panel>
          <Header title="Verify your channel" onClose={onClose} />
          <div className="px-5 pb-5 text-center space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Log in to verify a channel you control.
            </p>
            <button
              onClick={() => { setShowLoginDialog(true); onClose(); }}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold"
            >
              Log in
            </button>
          </div>
        </Panel>
      </Backdrop>,
      document.body,
    );
  }

  // ── Success ──
  if (verifiedChannel) {
    return createPortal(
      <Backdrop onClose={onClose}>
        <Panel>
          <Header title="Channel verified" onClose={onClose} />
          <div className="px-5 pb-5 text-center space-y-3">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <ShieldCheck className="w-7 h-7 text-emerald-500" />
            </div>
            <p className="text-sm font-bold text-emerald-500">
              {displayChannel(verifiedChannel)} is verified
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Every video you post from this channel now carries a
              <span className="inline-flex items-center gap-0.5 font-semibold text-foreground"> <BadgeCheck className="w-3 h-3 text-primary" /> Verified creator</span> badge.
              You can remove the token from your video description now if you like.
            </p>
            <button onClick={onClose} className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold">
              Done
            </button>
          </div>
        </Panel>
      </Backdrop>,
      document.body,
    );
  }

  // ── Form ──
  return createPortal(
    <Backdrop onClose={checking ? () => {} : onClose}>
      <Panel>
        <Header title="Verify your creator channel" onClose={checking ? undefined : onClose} />
        <div className="px-5 pb-5 space-y-4 max-h-[80vh] overflow-y-auto">
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Prove you control your <span className="font-semibold text-foreground">YouTube</span> or{' '}
            <span className="font-semibold text-foreground">Odysee</span> channel — once — and every
            video you post from it earns a{' '}
            <span className="font-semibold text-foreground">Verified creator</span> badge, so only
            you can receive tips on it. No effect on how you post videos.
          </p>

          {/* Step 1 */}
          <div className="space-y-1.5">
            <p className="text-xs font-bold">1. Copy your verification token</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 break-all text-[11px] font-mono bg-background rounded-lg px-2.5 py-2 border border-border">
                {token}
              </code>
              <button
                onClick={copyToken}
                className="shrink-0 px-2.5 py-2 rounded-lg bg-muted text-[11px] font-medium hover:bg-muted/70 flex items-center gap-1"
              >
                {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
            </div>
          </div>

          {/* Step 2 */}
          <div className="space-y-1">
            <p className="text-xs font-bold">2. Paste it into a video description</p>
            <p className="text-[11px] text-muted-foreground leading-relaxed">
              Add the token anywhere in the description of <strong>any public video</strong> on your
              YouTube or Odysee channel, and save. Only the channel owner can do this — that's the
              proof.
            </p>
          </div>

          {/* Step 3 */}
          <div className="space-y-1.5">
            <p className="text-xs font-bold">
              3. Paste that video's link and check
              {detected && (
                <span className="ml-1.5 font-normal text-[10px] text-primary">
                  · {detected} detected
                </span>
              )}
            </p>
            <input
              value={videoUrl}
              onChange={e => { setVideoUrl(e.target.value); setError(''); }}
              placeholder="https://youtube.com/watch?v=… or https://odysee.com/@channel/video"
              className="w-full bg-background rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
              inputMode="url"
              autoComplete="off"
              spellCheck={false}
            />
          </div>

          <button
            onClick={handleVerify}
            disabled={checking || !videoUrl.trim()}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary/90 disabled:opacity-50"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <BadgeCheck className="w-4 h-4" />}
            {checking ? 'Checking…' : 'Verify my channel'}
          </button>

          {error && (
            <p className="flex items-start gap-1.5 text-xs text-red-500">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {error}
            </p>
          )}

          <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
            The check reads only the public description of the video you name. BCHNostr never
            connects to your YouTube, Google or Odysee account.
          </p>
        </div>
      </Panel>
    </Backdrop>,
    document.body,
  );
}

// ── Chrome ────────────────────────────────────────────────────────

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      {children}
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-popover border border-border shadow-2xl overflow-hidden"
      onClick={e => e.stopPropagation()}
    >
      {children}
    </div>
  );
}

function Header({ title, onClose }: { title: string; onClose?: () => void }) {
  return (
    <div className="flex items-center justify-between px-5 pt-5 pb-3">
      <h2 className="font-bold text-base flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-emerald-600 flex items-center justify-center">
          <BadgeCheck className="w-4 h-4 text-white" />
        </div>
        {title}
      </h2>
      {onClose && (
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
